import { FC, useState } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { Plus, Star, Trash2, X } from 'lucide-react';
import { BorneDto, ModeCalculTarification, TypeBorne, UnitEnum } from 'api/funnel';
import { Button } from 'components/ui/Button';
import { Dropdown } from 'components/forms/Dropdown';
import { InputText } from 'components/forms/InputText';
import { FormField } from 'components/forms/FormField';
import { Input } from 'components/forms/fragments/Input';
import { RadioGroupComponent } from 'components/forms/RadioGroup';
import { UNIT_OPTIONS, unitSuffix } from '../funnelEnums';

/**
 * A borne always carries a pricing object for the back-end, which requires one on every
 * deductible and ceiling, but the engine never reads it: the value picked by the broker
 * reaches the guarantee formula through FRANCHISE_CLI / PLAFOND_CLI. It is kept hidden.
 */
const emptyBorne = (): BorneDto => ({
  designation: '',
  type: TypeBorne.Slider,
  valeurMinimale: 0,
  valeurMaximale: 0,
  valeurDefaut: null,
  valeurs: [],
  tarification: { modeCalcul: ModeCalculTarification.MontantFixe, montantFixe: 0 },
  duree: { value: 0, unite: UnitEnum.Amount },
});

const toItems = <T extends number>(options: { value: T; label: string }[]) =>
  options.map((option) => ({ key: option.value, label: option.label }));

const unitItems = toItems(UNIT_OPTIONS);

type ChoiceMode = 'slider' | 'list';

const CHOICE_OPTIONS: { value: ChoiceMode; label: string }[] = [
  { value: 'slider', label: 'Curseur entre un minimum et un maximum' },
  { value: 'list', label: 'Liste de valeurs' },
];

interface BorneListProps {
  /** Formik field holding the array: "franchises" or "plafonds". */
  name: 'franchises' | 'plafonds';
  addLabel: string;
}

/**
 * Deductibles or ceilings of a guarantee. Both share the same back-end contract
 * (BorneDto) and the same form.
 */
export const BorneList: FC<BorneListProps> = ({ name, addLabel }) => {
  const { values, setFieldValue, getFieldMeta, submitCount } =
    useFormikContext<Record<string, BorneDto[]>>();
  const bornes = values[name] ?? [];
  const isFranchise = name === 'franchises';

  return (
    <FieldArray name={name}>
      {({ push, remove }) => (
        <div className="flex flex-col gap-4">
          {bornes.map((borne, index) => (
            <BorneCard
              key={index}
              borne={borne}
              prefix={`${name}.${index}`}
              isFranchise={isFranchise}
              onRemove={() => remove(index)}
              setFieldValue={setFieldValue}
              errorOf={(field) =>
                submitCount > 0 ? getFieldMeta(`${name}.${index}.${field}`).error : undefined
              }
            />
          ))}

          <Button plain onClick={() => push(emptyBorne())}>
            <Plus data-slot="icon" />
            {addLabel}
          </Button>
        </div>
      )}
    </FieldArray>
  );
};

interface BorneCardProps {
  borne: BorneDto;
  prefix: string;
  isFranchise: boolean;
  onRemove: () => void;
  setFieldValue: (field: string, value: unknown) => void;
  errorOf: (field: string) => string | undefined;
}

const BorneCard: FC<BorneCardProps> = ({
  borne,
  prefix,
  isFranchise,
  onRemove,
  setFieldValue,
  errorOf,
}) => {
  const [newValue, setNewValue] = useState('');

  const isList = borne.type === TypeBorne.List;
  const unite = borne.duree?.unite ?? UnitEnum.Amount;
  const suffix = unitSuffix(unite);
  const valeurs = borne.valeurs ?? [];
  const noun = isFranchise ? 'franchise' : 'plafond';
  const variableCode = isFranchise ? 'VAR:FRANCHISE_CLI' : 'VAR:PLAFOND_CLI';

  const setNumber = (field: string, raw: string) =>
    setFieldValue(`${prefix}.${field}`, raw === '' ? null : Number(raw));

  const addValue = () => {
    const parsed = Number(newValue.trim().replace(',', '.'));
    if (!Number.isFinite(parsed) || newValue.trim() === '') return;
    const next = valeurs.includes(parsed) ? valeurs : [...valeurs, parsed].sort((a, b) => a - b);
    setFieldValue(`${prefix}.valeurs`, next);
    // The first value of a list becomes the default so the borne is always usable.
    if (borne.valeurDefaut == null) setFieldValue(`${prefix}.valeurDefaut`, parsed);
    setNewValue('');
  };

  const removeValue = (valeur: number) => {
    setFieldValue(
      `${prefix}.valeurs`,
      valeurs.filter((v) => v !== valeur)
    );
    if (borne.valeurDefaut === valeur) setFieldValue(`${prefix}.valeurDefaut`, null);
  };

  const min = borne.valeurMinimale ?? 0;
  const max = borne.valeurMaximale ?? 0;
  const defaut = borne.valeurDefaut ?? min;

  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <span className="truncate text-sm font-semibold text-zinc-800">
          {borne.designation?.trim() || (isFranchise ? 'Nouvelle franchise' : 'Nouveau plafond')}
        </span>
        <button
          type="button"
          onClick={onRemove}
          aria-label={isFranchise ? 'Enlever la franchise' : 'Enlever le plafond'}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Enlever
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <InputText
            name={`${prefix}.designation`}
            label="Désignation"
            required
            value={borne.designation ?? ''}
            error={errorOf('designation')}
            onChange={(event) => setFieldValue(`${prefix}.designation`, event.target.value)}
          />
        </div>

        <Dropdown
          name={`${prefix}.duree.unite`}
          label="Unité"
          required
          items={unitItems}
          selectedItem={unitItems.find((item) => item.key === unite) ?? null}
          onSelect={(item) => setFieldValue(`${prefix}.duree.unite`, item.key)}
        />
      </div>

      <div className="mt-4">
        <RadioGroupComponent<ChoiceMode>
          name={`${prefix}.type`}
          label={`Comment le courtier choisit la ${noun}`}
          required
          options={CHOICE_OPTIONS}
          value={isList ? 'list' : 'slider'}
          onChange={(mode) =>
            setFieldValue(`${prefix}.type`, mode === 'list' ? TypeBorne.List : TypeBorne.Slider)
          }
        />
      </div>

      {!isList ? (
        <div className="mt-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <NumberWithSuffix
              name={`${prefix}.valeurMinimale`}
              label="Minimum"
              suffix={suffix}
              value={borne.valeurMinimale ?? ''}
              error={errorOf('valeurMinimale')}
              onChange={(raw) => setNumber('valeurMinimale', raw)}
            />
            <NumberWithSuffix
              name={`${prefix}.valeurDefaut`}
              label="Par défaut"
              suffix={suffix}
              value={borne.valeurDefaut ?? ''}
              error={errorOf('valeurDefaut')}
              onChange={(raw) => setNumber('valeurDefaut', raw)}
            />
            <NumberWithSuffix
              name={`${prefix}.valeurMaximale`}
              label="Maximum"
              suffix={suffix}
              value={borne.valeurMaximale ?? ''}
              error={errorOf('valeurMaximale')}
              onChange={(raw) => setNumber('valeurMaximale', raw)}
            />
          </div>

          {/* Read-only preview of the slider the broker will see, with the default position. */}
          {max > min && (
            <div className="mt-3 flex items-center gap-3 text-xs text-gray-400">
              <span className="shrink-0">
                {min} {suffix}
              </span>
              <input
                type="range"
                readOnly
                tabIndex={-1}
                min={min}
                max={max}
                value={Math.min(Math.max(defaut, min), max)}
                aria-label="Aperçu du curseur"
                className="w-full accent-primary"
              />
              <span className="shrink-0">
                {max} {suffix}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-4">
          <FormField label="Valeurs proposées" required name={`${prefix}.valeurs`} error={errorOf('valeurs') ?? errorOf('valeurDefaut')}>
            <div className="flex flex-wrap items-center gap-2">
              {valeurs.map((valeur) => {
                const isDefault = borne.valeurDefaut === valeur;
                return (
                  <span
                    key={valeur}
                    className={`inline-flex items-center overflow-hidden rounded-full border text-sm ${
                      isDefault
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-gray-200 bg-white text-zinc-800'
                    }`}
                  >
                    <button
                      type="button"
                      title="Définir comme valeur par défaut"
                      onClick={() => setFieldValue(`${prefix}.valeurDefaut`, valeur)}
                      className="inline-flex items-center gap-1 py-1 pl-2.5 pr-1.5 font-semibold hover:bg-gray-50"
                    >
                      {isDefault && <Star className="h-3 w-3 fill-current" />}
                      {valeur} {suffix}
                    </button>
                    <button
                      type="button"
                      aria-label={`Retirer la valeur ${valeur}`}
                      onClick={() => removeValue(valeur)}
                      className="px-1.5 py-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}

              <div className="flex items-center gap-1.5">
                <Input
                  name={`${prefix}.newValeur`}
                  type="number"
                  placeholder="Valeur"
                  value={newValue}
                  onChange={(event) => setNewValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addValue();
                    }
                  }}
                  className="w-28"
                />
                <Button plain onClick={addValue} disabled={newValue.trim() === ''}>
                  <Plus data-slot="icon" />
                  Ajouter
                </Button>
              </div>
            </div>
          </FormField>
          <p className="mt-1.5 text-xs text-gray-400">
            Cliquer une valeur pour en faire la valeur par défaut (étoile).
          </p>
        </div>
      )}

      <p className="mt-4 text-xs text-gray-400">
        Dans la formule de la garantie, la valeur choisie par le courtier est{' '}
        <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[11px] text-gray-600">
          {variableCode}
        </code>
        .
      </p>
    </div>
  );
};

interface NumberWithSuffixProps {
  name: string;
  label: string;
  suffix: string;
  value: number | '';
  error?: string;
  onChange: (raw: string) => void;
}

const NumberWithSuffix: FC<NumberWithSuffixProps> = ({ name, label, suffix, value, error, onChange }) => (
  <FormField label={label} required name={name} error={error}>
    <div className="relative">
      <Input
        id={name}
        name={name}
        type="number"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="pr-14"
      />
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-400">
        {suffix}
      </span>
    </div>
  </FormField>
);
